import { logger } from "./logger";

/**
 * StorageArray - Array that persists to localStorage
 *
 * Extends the native Array class to provide automatic
 * loading from and saving to localStorage.
 */
export class StorageArray<T> {
  private _storageKey: string = "";
  static SESSION_KEY = "ascrtip_session";

  private values = [] as T[];

  get storageKey(): string {
    if (!this._storageKey) {
      const sessionStorageSession = sessionStorage.getItem(
        StorageArray.SESSION_KEY
      );

      if (sessionStorageSession) {
        this._storageKey = sessionStorageSession;
      } else {
        // If no session storage key is set, use the default
        this._storageKey = Date.now().toString();
        sessionStorage.setItem(StorageArray.SESSION_KEY, this._storageKey);
      }

      logger.debug("Using storage key: " + this._storageKey);
    }

    return `ascript_` + this._storageKey;
  }

  /**
   * Create a new StorageArray
   *
   * @param storageKey - Key to use for localStorage
   */
  constructor(storageKey: string) {
    logger.debug("Creating new StorageArray with key: " + storageKey);

    this.load();
  }

  /**
   * Push items to the array and save to localStorage
   */
  push(...items: T[]): number {
    logger.debug("Pushing items to StorageArray", { items });

    this.load(); // Load existing items before pushing new ones

    this.values = this.values.concat(items);
    const result = this.values.length;
    this.save();
    return result;
  }

  /**
   * Remove elements from array and optionally insert new elements
   */
  splice(start: number, deleteCount?: number, ...items: T[]): T[] {
    logger.debug("Splicing StorageArray", {
      start,
      deleteCount,
      itemsCount: items.length,
    });

    this.load(); // Load existing items before splicing

    const removed = this.values.splice(start, deleteCount || 0, ...items);
    this.save();
    return removed;
  }

  /**
   * Save current array state to localStorage
   */
  private save(): void {
    logger.debug("Saving StorageArray state to localStorage");

    if (typeof localStorage !== "undefined") {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify(Array.from(this.values))
      );
    }
  }

  /**
   * Load array state from localStorage
   */
  private load(): void {
    logger.debug("Loading StorageArray state from localStorage");

    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem(this.storageKey);

      if (stored) {
        const data = JSON.parse(stored) as T[];
        logger.debug("Loaded data from localStorage: ", { data });
        this.values.length = 0; // Clear existing items
        this.values = this.values.concat(data);
      }
    }
  }
}

// Example usage:
// const myArray = new StorageArray<string>('my-array-key');
// myArray.push('hello'); // Automatically saves to localStorage
